// admin/src/lib/poster.ts
//
// Captures a poster frame from a video File at ~1s, plus reads its
// duration, via an offscreen <video> + <canvas> (wave 10 spec §3 Lane D:
// "a poster frame captured client-side at ~1 s"). Browser-only (no DOM in
// vitest's default environment). The frame is captured at full size; the
// caller compresses it like any other photo (./image.ts).

export type VideoInfo = { durationS: number; poster: Blob };

export async function captureVideoPoster(file: Blob, atSeconds = 1): Promise<VideoInfo> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.src = url;

  try {
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Could not read video metadata'));
    });

    const durationS = Number.isFinite(video.duration) ? video.duration : 0;
    const seekTo = Math.min(Math.max(atSeconds, 0), Math.max(durationS - 0.05, 0));

    await new Promise<void>((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error('Could not seek video'));
      video.currentTime = seekTo;
    });

    const width = video.videoWidth;
    const height = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    ctx.drawImage(video, 0, 0, width, height);

    const poster = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!poster) throw new Error('canvas.toBlob returned null');

    return { durationS, poster };
  } finally {
    URL.revokeObjectURL(url);
  }
}
